/**
 * Fire-and-forget client-side failure reporting.
 *
 * Why this exists:
 *   sld-api/pdf.php can only log failures that actually REACH it (e.g.
 *   Gotenberg down → 502). But the failures a user screenshot usually
 *   shows are the ones that never get there — a 404 because sld-api/
 *   isn't where the bundle expects it, a WAF/ModSecurity 403 eating the
 *   multipart POST, or a plain network error. This beacon posts those to
 *   a tiny JSON endpoint (log-client-error.php) which records them in
 *   the ai_usage audit stream and raises a throttled admin alert email,
 *   so "PDF export failed" screenshots from clients stop being the only
 *   diagnostic we have.
 *
 * Guarantees:
 *   - Never throws, never blocks the caller (keepalive fetch, all
 *     errors swallowed). A failed report is silently dropped — if the
 *     server is unreachable enough that even this JSON POST dies, the
 *     pdf.php failure path can't have logged anything either, and
 *     there's nothing more we can do from the browser.
 */
import { sldApiUrl } from './sldApiUrl';

export interface ClientErrorReport {
  /** Short machine-ish context tag, e.g. 'pdf_export'. */
  context: string;
  /** The error message (already includes status + endpoint for export
   *  failures — see exportPdf.ts's thrown Error). */
  message: string;
  /** SP project id when the editor was opened from a real project
   *  (?project=<qid> — the store keeps it as a string); null in
   *  standalone/free-tier mode. */
  projectId?: string | number | null;
}

export function reportSldClientError(r: ClientErrorReport): void {
  try {
    const payload = {
      context: String(r.context || 'unknown').slice(0, 48),
      message: String(r.message || '').slice(0, 900),
      projectId: r.projectId ?? null,
      url: typeof window !== 'undefined' ? window.location.href.slice(0, 400) : '',
      ua: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : '',
    };
    // keepalive lets the request survive a page nav right after the
    // failure dialog; .catch swallows network errors (see doc block).
    void fetch(sldApiUrl('log-client-error.php'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      /* reporting must never break the app */
    });
  } catch {
    /* reporting must never break the app */
  }
}
